// src/Admin.jsx
// Owner portal: add, edit, hide, reorder and remove fleet cars.
// Changes stay on this page until "Publish changes" writes /data/cars.json.
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { loadAllCars, setCarsCache } from './data/useCars';
import { getToken, saveCars, signOut, uploadPhoto } from './data/adminApi';
import { TIERS } from './data/pricing';

const EMPTY = { name: '', link: '', tag: '', tier: 'economy', image: '', visible: true };

export default function Admin() {
  const navigate = useNavigate();
  const [cars, setCars] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [editing, setEditing] = useState(null); // car id, 'new' or null
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [status, setStatus] = useState({ kind: '', text: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadAllCars().then(setCars);
  }, []);

  // Warn before closing the tab with unpublished changes
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const tags = useMemo(() => Array.from(new Set((cars || []).map((c) => c.tag).filter(Boolean))).sort(), [cars]);

  if (!getToken()) return <Navigate to="/contact" replace />;

  function change(next) {
    setCars(next);
    setDirty(true);
    setStatus({ kind: '', text: '' });
  }

  function move(index, delta) {
    const next = [...cars];
    const [car] = next.splice(index, 1);
    next.splice(index + delta, 0, car);
    change(next);
  }

  function applyEdit(form) {
    if (editing === 'new') {
      change([{ ...form, id: `car-${Date.now()}` }, ...cars]);
    } else {
      change(cars.map((c) => (c.id === editing ? { ...c, ...form } : c)));
    }
    setEditing(null);
  }

  async function publish() {
    setSaving(true);
    setStatus({ kind: '', text: '' });
    try {
      const list = cars.map((c, i) => ({ ...c, order: i }));
      await saveCars(list);
      setCarsCache(list);
      setDirty(false);
      setStatus({ kind: 'ok', text: 'Published. The live site shows these cars now.' });
    } catch (err) {
      if (err.status === 401) {
        setStatus({ kind: 'error', text: 'You were signed out. Sign in again from the Contact page; your changes on this page are lost if you leave.' });
      } else {
        setStatus({ kind: 'error', text: err.message });
      }
    } finally {
      setSaving(false);
    }
  }

  function leave() {
    signOut();
    navigate('/contact');
  }

  const editingCar = editing === 'new' ? EMPTY : cars && cars.find((c) => c.id === editing);

  return (
    <div className="container-x section-y" style={{ maxWidth: '56rem' }}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="display text-4xl">Fleet admin</h1>
          <p className="muted mt-2">
            {cars ? `${cars.length} cars, ${cars.filter((c) => c.visible === false).length} hidden` : 'Loading cars'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn btn-primary" onClick={() => setEditing('new')} disabled={!cars}>
            Add a car
          </button>
          <button type="button" className="btn btn-ghost" onClick={leave}>
            Sign out
          </button>
        </div>
      </div>

      {editingCar && (
        <CarForm
          key={editing}
          initial={editingCar}
          isNew={editing === 'new'}
          tags={tags}
          onCancel={() => setEditing(null)}
          onDone={applyEdit}
        />
      )}

      {cars && (
        <ul className="mt-8" style={{ borderTop: '1px solid var(--line)' }}>
          {cars.map((car, i) => {
            const hidden = car.visible === false;
            return (
              <li
                key={car.id}
                className="flex flex-wrap items-center gap-4 py-4"
                style={{ borderBottom: '1px solid var(--line)', opacity: hidden ? 0.55 : 1 }}
              >
                <img src={car.image} alt="" className="h-16 w-24 shrink-0 rounded-lg object-cover" />
                <div className="min-w-0 flex-1" style={{ flexBasis: '12rem' }}>
                  <p className="font-semibold">
                    {car.name}
                    {hidden && <span className="muted ml-2 text-sm font-normal">(hidden)</span>}
                  </p>
                  <p className="muted text-sm">
                    {car.tag || 'No tag'} · {(TIERS.find((t) => t.key === car.tier) || {}).label || 'No fee class'}
                  </p>
                </div>

                {confirmDelete === car.id ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm">Remove this car?</span>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        change(cars.filter((c) => c.id !== car.id));
                        setConfirmDelete(null);
                      }}
                    >
                      Remove
                    </button>
                    <button type="button" className="btn btn-ghost" onClick={() => setConfirmDelete(null)}>
                      Keep
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    <IconButton label="Move up" disabled={i === 0} onClick={() => move(i, -1)}>
                      ↑
                    </IconButton>
                    <IconButton label="Move down" disabled={i === cars.length - 1} onClick={() => move(i, 1)}>
                      ↓
                    </IconButton>
                    <SmallButton onClick={() => setEditing(car.id)}>Edit</SmallButton>
                    <SmallButton
                      onClick={() => change(cars.map((c) => (c.id === car.id ? { ...c, visible: hidden } : c)))}
                    >
                      {hidden ? 'Show' : 'Hide'}
                    </SmallButton>
                    <SmallButton onClick={() => setConfirmDelete(car.id)}>Remove</SmallButton>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Publish bar */}
      {(dirty || status.text) && (
        <div
          className="fixed inset-x-0 bottom-0 z-40"
          style={{
            background: 'var(--surface)',
            borderTop: '1px solid var(--line)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          <div className="container-x flex flex-wrap items-center justify-between gap-3 py-4" style={{ maxWidth: '56rem' }}>
          <p
            role="status"
            className="text-sm"
            style={{ color: status.kind === 'error' ? '#f0a08a' : status.kind === 'ok' ? '#9fd6a4' : 'var(--muted)' }}
          >
            {status.text || 'You have changes that are not on the live site yet.'}
          </p>
          {dirty && (
            <button type="button" className="btn btn-primary" onClick={publish} disabled={saving}>
              {saving ? 'Publishing' : 'Publish changes'}
            </button>
          )}
          </div>
        </div>
      )}
      {/* Room so the fixed bar never covers the last car */}
      <div className="h-24" aria-hidden="true" />
    </div>
  );
}

function CarForm({ initial, isNew, tags, onCancel, onDone }) {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  async function pickPhoto(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const url = await uploadPhoto(file);
      setForm((f) => ({ ...f, image: url }));
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  function submit(e) {
    e.preventDefault();
    if (!form.image) return setError('Add a photo first.');
    if (form.link && !/^https:\/\//.test(form.link.trim())) return setError('The Turo link should start with https://');
    onDone({ ...form, name: form.name.trim(), link: form.link.trim(), tag: form.tag.trim() });
  }

  return (
    <form onSubmit={submit} className="pfr-card mt-8 p-5 sm:p-6">
      <h2 className="display text-2xl">{isNew ? 'Add a car' : `Edit ${initial.name}`}</h2>

      <div className="mt-5 grid gap-5 sm:grid-cols-[12rem_1fr]">
        <div>
          <span className="field-label">Photo</span>
          <div
            className="flex aspect-[4/3] items-center justify-center overflow-hidden rounded-lg"
            style={{ background: 'var(--surface-2)', border: '1px solid var(--line)' }}
          >
            {form.image ? (
              <img src={form.image} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="muted text-sm">{uploading ? 'Uploading' : 'No photo yet'}</span>
            )}
          </div>
          <label className="btn btn-ghost mt-3 w-full">
            {uploading ? 'Uploading' : form.image ? 'Change photo' : 'Choose photo'}
            <input type="file" accept="image/*" className="sr-only" onChange={pickPhoto} disabled={uploading} />
          </label>
        </div>

        <div className="grid content-start gap-4">
          <div>
            <label className="field-label" htmlFor="car-name">
              Name
            </label>
            <input id="car-name" className="field" placeholder="2024 Toyota Camry" value={form.name} onChange={set('name')} required />
          </div>
          <div>
            <label className="field-label" htmlFor="car-link">
              Turo listing link
            </label>
            <input
              id="car-link"
              className="field"
              type="url"
              placeholder="https://turo.com/us/en/car-rental/..."
              value={form.link}
              onChange={set('link')}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="field-label" htmlFor="car-tag">
                Type (shown on the site)
              </label>
              <input id="car-tag" className="field" list="car-tags" placeholder="SUV" value={form.tag} onChange={set('tag')} />
              <datalist id="car-tags">
                {tags.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="field-label" htmlFor="car-tier">
                Fee class
              </label>
              <select id="car-tier" className="field" value={form.tier} onChange={set('tier')}>
                {TIERS.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-3">
            <input type="checkbox" checked={form.visible !== false} onChange={set('visible')} className="h-5 w-5" />
            Show on the site
          </label>
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-4 text-sm" style={{ color: '#f0a08a' }}>
          {error}
        </p>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        <button type="submit" className="btn btn-primary" disabled={uploading}>
          {isNew ? 'Add to list' : 'Done'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function SmallButton({ children, ...props }) {
  return (
    <button
      type="button"
      className="inline-flex min-h-[2.5rem] items-center rounded-full px-3 text-sm font-medium hover:bg-white/10"
      style={{ border: '1px solid var(--line)' }}
      {...props}
    >
      {children}
    </button>
  );
}

function IconButton({ label, children, ...props }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className="inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-white/10 disabled:opacity-30"
      style={{ border: '1px solid var(--line)' }}
      {...props}
    >
      {children}
    </button>
  );
}
